import { SaudiRiyal } from "lucide-react";
import { HTMLAttributes } from "react";

import { useLanguage } from "@/contexts/language";
import { cn } from "@/lib/utils";
import { getSession } from "@/session";

interface CurrencyProps extends HTMLAttributes<SVGElement | HTMLSpanElement> {
  currency?: string;
}

type CurrencySymbol = string | { rtl: string; ltr: string };

const CURRENCY_SYMBOLS: Record<string, CurrencySymbol> = {
  // Major Global
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CHF: "CHF",

  // Gulf & Middle East
  AED: "د.إ",
  KWD: { rtl: "د.ك", ltr: "KWD" },
  BHD: { rtl: "د.ب", ltr: "BHD" },
  OMR: { rtl: "ر.ع", ltr: "OMR" },
  QAR: { rtl: "ر.ق", ltr: "QAR" },
  JOD: { rtl: "د.أ", ltr: "JOD" },
  LBP: { rtl: "ل.ل", ltr: "LBP" },
  IQD: { rtl: "د.ع", ltr: "IQD" },
  SYP: { rtl: "ل.س", ltr: "SYP" },
  YER: { rtl: "ر.ي", ltr: "YER" },
  ILS: "₪",

  // Asia Pacific
  CNY: "¥",
  INR: "₹",
  KRW: "₩",
  SGD: "S$",
  HKD: "HK$",
  THB: "฿",
  MYR: "RM",
  IDR: "Rp",
  PHP: "₱",
  VND: "₫",
  PKR: "₨",
  BDT: "৳",
  LKR: "Rs",
  MMK: "K",
  NPR: "Rs",

  // Americas
  CAD: "C$",
  MXN: "$",
  BRL: "R$",
  ARS: "$",
  CLP: "$",
  COP: "$",
  PEN: "S/",

  // Europe
  RUB: "₽",
  PLN: "zł",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  CZK: "Kč",
  HUF: "Ft",
  RON: "lei",
  BGN: "лв",
  HRK: "kn",
  UAH: "₴",
  RSD: "din",
  MKD: "ден",
  ALL: "L",
  BAM: "KM",

  // Central Asia & Caucasus
  KZT: "₸",
  UZS: "soʻm",
  TJS: "ЅМ",
  KGS: "с",
  TMT: "m",
  AZN: "₼",
  GEL: "₾",
  AMD: "֏",

  // Africa
  EGP: { rtl: "ج.م", ltr: "£" },
  ZAR: "R",
  NGN: "₦",
  KES: "KSh",
  GHS: "₵",
  TZS: "TSh",
  UGX: "USh",
  MAD: { rtl: "د.م.", ltr: "MAD" },
  TND: { rtl: "د.ت", ltr: "TND" },
  DZD: { rtl: "د.ج", ltr: "DZD" },
  LYD: { rtl: "ل.د", ltr: "LYD" },
  SDG: { rtl: "ج.س", ltr: "SDG" },
  ETB: "Br",
  XOF: "CFA",
  XAF: "FCFA",
  ZMW: "ZK",
  BWP: "P",
  MUR: "₨",
  AOA: "Kz",
  MZN: "MT",
  RWF: "FRw",
  SCR: "₨",
  SOS: "Sh",
  SZL: "L",
  MWK: "MK",
  LSL: "L",
  GMD: "D",
  GNF: "FG",
  SLL: "Le",
  LRD: "L$",

  // Southeast Asia (additional)
  KHR: "៛",
  LAK: "₭",
  BND: "B$",
  TWD: "NT$",
  MOP: "MOP$",

  // Oceania
  AUD: "A$",
  NZD: "NZ$",
  FJD: "FJ$",
  PGK: "K",
  TOP: "T$",
  WST: "WS$",
  VUV: "VT",

  // Caribbean & Central America
  JMD: "J$",
  TTD: "TT$",
  BSD: "B$",
  BBD: "Bds$",
  XCD: "EC$",
  CRC: "₡",
  GTQ: "Q",
  HNL: "L",
  NIO: "C$",
  PAB: "B/.",
  DOP: "RD$",
  HTG: "G",
  CUP: "₱",

  // South America (additional)
  UYU: "$U",
  PYG: "₲",
  BOB: "Bs.",
  VES: "Bs.S",
  GYD: "G$",
  SRD: "Sr$",

  // Turkey
  TRY: "₺",

  // Iran & Afghanistan
  IRR: { rtl: "﷼", ltr: "IRR" },
  AFN: "؋",
};

export const Currency = ({
  className,
  style,
  currency,
  ...props
}: CurrencyProps) => {
  const { dir } = useLanguage();

  const currencyCode = currency?.toUpperCase() ?? getSession()?.currency;

  if (currencyCode === "SAR") {
    return <SaudiRiyal className={className} style={style} {...props} />;
  }

  const symbolConfig = CURRENCY_SYMBOLS[currencyCode || ""];

  let symbol: string;
  if (typeof symbolConfig === "object") {
    symbol = symbolConfig[dir];
  } else {
    symbol = symbolConfig || currencyCode || "";
  }

  return (
    <span
      className={cn("tw:!my-0 tw:!size-auto", className)}
      style={style}
      {...props}
    >
      {symbol}
    </span>
  );
};
